// src/api/panel/useAdminUserProfiles.js
import dynamicAPI from '../dynamicAPI';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMessage } from '../../context/AdminMessageContext';

const urlUserProfile = '/api/auth/user-profiles';

export default function useAdminUserProfiles({ page = 1, q = '', limit = 25 } = {}) {
    const queryClient = useQueryClient();
    const { setSuccessMessage, setErrorMessage } = useMessage();

    // list of profiles
    const getUserProfiles = useQuery(
        ['userProfiles', page, q, limit],
        async () =>
            await dynamicAPI(
                'get',
                `${urlUserProfile}?page=${page}&q=${encodeURIComponent(q)}&limit=${limit}`,
                {}
            ),
        {
            retry: 0,
            keepPreviousData: true,
        }
    );

    // generate all missing profiles (for users without one)
    const generateMissingProfiles = useMutation(
        async () => await dynamicAPI('post', `${urlUserProfile}/generate-missing`),
        {
            retry: 0,
            onSuccess: (data) => {
                setSuccessMessage(
                    data?.message || 'Missing profiles generated successfully.'
                );
                queryClient.invalidateQueries(['userProfiles']);
            },
            onError: (error) => {
                const msg =
                    error?.error || 'An error occurred while generating profiles.';
                setErrorMessage(msg);
            },
        }
    );

    return {
        getUserProfiles,
        generateMissingProfiles,
    };
}

/**
 * Admin: fetch/update a specific user's profile by userId
 * Uses /api/auth/user-profiles/:id
 */
export function useAdminUserProfile(userId) {
    const queryClient = useQueryClient();
    const { setSuccessMessage, setErrorMessage } = useMessage();

    const getAdminUserProfile = useQuery(
        ['adminUserProfile', userId],
        async () => {
            const response = await dynamicAPI('get', `${urlUserProfile}/${userId}`);
            if (response.error) {
                const errorMessage =
                    response.error ||
                    'An error occurred while fetching user profile.';
                setErrorMessage(errorMessage);
                throw new Error(errorMessage);
            }
            return response;
        },
        {
            enabled: !!userId,
            retry: 0,
            staleTime: 1000 * 15,
        }
    );

    const postAdminUserProfile = useMutation(
        async (obj) => await dynamicAPI('post', `${urlUserProfile}/${userId}`, obj),
        {
            retry: 0,
            onSuccess: (data) => {
                if (data.isSuccess || !data.error) {
                    setSuccessMessage('User profile updated successfully.');
                    queryClient.invalidateQueries(['adminUserProfile', userId]);
                    queryClient.invalidateQueries(['userProfiles']);
                } else {
                    const errorMessage =
                        data.error || 'Failed to update user profile.';
                    setErrorMessage(errorMessage);
                }
            },
            onError: (error) => {
                const errorMessage =
                    error?.error ||
                    'An error occurred while updating the user profile.';
                setErrorMessage(errorMessage);
            },
        }
    );

    return { getAdminUserProfile, postAdminUserProfile };
}
